import React, { useEffect, useState } from "react";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { Loader2, Store, Banknote, Clock, Check, X } from "lucide-react";
import { GlassButton, GlassField } from "@/components/GlassBits";
import { useUserAuth } from "@/context/UserAuthContext";

const CHIP = {
  PENDIENTE: "text-amber-300",
  APROBADO: "text-emerald-400",
  PAGADO: "text-emerald-400",
  RECHAZADO: "text-rose-400",
};

export const UserMarketWallet = () => {
  const { refresh } = useUserAuth();
  const [deals, setDeals] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [amount, setAmount] = useState("");
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [d, p, q] = await Promise.all([
        userApi.get("/market/deals/mine"),
        userApi.get("/market/payouts/mine"),
        userApi.get("/market/payouts/quote?credits=0"),
      ]);
      setDeals(d.data.deals); setPayouts(p.data.payouts); setQuote(q.data);
    } catch (e) {
      toast.error(apiError(e, "No se pudo cargar tu mercado"));
      setDeals([]);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const n = parseInt(amount, 10);
    if (!n || n < 1) {
      userApi.get("/market/payouts/quote?credits=0")
        .then(({ data }) => setQuote(data)).catch(() => {});
      return;
    }
    userApi.get(`/market/payouts/quote?credits=${n}`)
      .then(({ data }) => setQuote(data)).catch(() => setQuote(null));
  }, [amount]);

  const requestPayout = async () => {
    setBusy(true);
    try {
      const { data } = await userApi.post("/market/payouts", { credits: parseInt(amount, 10) });
      toast.success(`Cobro enviado: $${data.cash_mxn.toLocaleString()} MXN por pagar`);
      setAmount(""); await refresh(); await load();
    } catch (e) { toast.error(apiError(e, "No se pudo pedir el cobro")); }
    finally { setBusy(false); }
  };

  if (!deals) return <Loader2 className="w-6 h-6 animate-spin text-red-400" />;

  return (
    <div className="space-y-6" data-testid="user-market-wallet">
      <div className="glass-card forge-card p-6">
        <div className="flex items-center gap-2 text-white font-semibold">
          <Banknote className="w-5 h-5 text-amber-300" /> Cambiar créditos por dinero
        </div>
        <p className="text-sm text-white/65 mt-2 leading-relaxed">
          Solo puedes cambiar lo ganado en ventas aprobadas. Disponible:{" "}
          <span className="text-amber-300 font-semibold" data-testid="sellable-balance">
            {quote?.available ?? 0} créditos
          </span>. El administrador te transfiere por fuera y se descuenta un 15% al cambiar.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1">
            <GlassField label="Créditos a cambiar" value={amount} inputMode="numeric"
              data-testid="payout-amount-input" placeholder="Ej. 100"
              onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} />
          </div>
          <div className="sm:w-48 shrink-0">
            <GlassButton tone="red" icon={Banknote} loading={busy}
              disabled={!quote || quote.credits < 1 || quote.credits > quote.available}
              data-testid="payout-request-btn" onClick={requestPayout}>
              Pedir cobro
            </GlassButton>
          </div>
        </div>
        {quote && quote.credits > 0 && (
          <div className="text-xs text-white/70 mt-3" data-testid="payout-quote">
            Recibirás <span className="text-amber-300 font-semibold">
              ${quote.cash_mxn.toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN</span> por {quote.credits} créditos
            (comisión {quote.fee_pct}%).
          </div>
        )}
      </div>

      {payouts.length > 0 && (
        <div>
          <div className="text-white font-semibold mb-3">Mis cobros</div>
          <div className="space-y-2">
            {payouts.map((p) => (
              <div key={p.payout_id} className="glass-card px-4 py-3 flex items-center justify-between text-xs"
                data-testid={`payout-row-${p.payout_id}`}>
                <span className="text-white/75">
                  {p.credits} créditos · ${p.cash_mxn.toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN
                </span>
                <span className={CHIP[p.status] || "text-white/50"}>{p.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="text-white font-semibold mb-3 flex items-center gap-2">
          <Store className="w-4 h-4 text-amber-300" /> Mis compras y ventas
        </div>
        {deals.length === 0 && (
          <p className="text-sm text-white/50">Todavía no tienes tratos con créditos.</p>
        )}
        <div className="space-y-2">
          {deals.map((d) => (
            <div key={d.deal_id} className="glass-card px-4 py-3 flex items-center justify-between gap-3"
              data-testid={`deal-row-${d.deal_id}`}>
              <div className="min-w-0">
                <div className="text-sm text-white truncate">{d.title}</div>
                <div className="text-[11px] text-white/45 mt-0.5">
                  {d.role === "buyer"
                    ? (d.status === "RECHAZADO"
                        ? `Compra rechazada · se te devolvieron ${d.buyer_paid} créditos`
                        : `Compraste · pagaste ${d.buyer_paid} créditos`)
                    : (d.status === "RECHAZADO"
                        ? "Venta rechazada · no se abonó nada"
                        : d.status === "APROBADO"
                          ? `Vendiste · recibiste ${d.seller_credits} créditos`
                          : `Vendiste · recibirás ${d.seller_credits} créditos`)}
                </div>
              </div>
              <span className={`text-xs shrink-0 flex items-center gap-1 ${CHIP[d.status] || "text-white/50"}`}>
                {d.status === "PENDIENTE" && <Clock className="w-3.5 h-3.5" />}
                {d.status === "APROBADO" && <Check className="w-3.5 h-3.5" />}
                {d.status === "RECHAZADO" && <X className="w-3.5 h-3.5" />}
                {d.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserMarketWallet;
